<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import useLocale from './helpers/use-locale';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

type Props = {
  textToCopy: string;
  buttonText?: string;
  attach?: string | HTMLElement;
  iconCopied?: string;
  iconCopy?: string;
};
const props = withDefaults(defineProps<Props>(), {
  iconCopied: 'fas fa-check',
  iconCopy: 'fal fa-clone',
});

const emit = defineEmits<{ (e: 'copy'): void }>();

const { t } = useLocale();
const { mdAndDown, mdAndUp, platform } = useDisplay();

const isCopied = ref(false);
const isOpen = ref(false);
watch(() => props.textToCopy, () => (isCopied.value = false));
const isTouchDevice = computed(() => platform.value.touch);
const copyText = computed(() => (isCopied.value ? 'tooltip_copied' : 'tooltip_copy'));
const handleCopy = () => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(props.textToCopy).then(() => {
      isCopied.value = true;
    });
  } else {
    const textArea = document.createElement('textarea');
    textArea.value = props.textToCopy;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    isCopied.value = true;
    document.body.removeChild(textArea);
  }
  emit('copy');
};

const onToggleToolTip = (state: boolean) => {
  if (!state && isCopied) {
    // setTimeout to avoid ugly animation
    setTimeout(() => {
      isCopied.value = false;
    }, 300);
    return;
  }
  if (mdAndDown.value) {
    setTimeout(() => {
      isOpen.value = false;
    }, 2000);
  }
};
</script>

<template>
  <v-tooltip
    v-model="isOpen"
    :attach="props.attach || 'body'"
    :disabled="isTouchDevice || !!buttonText"
    :text="t(copyText)"
    location="top"
    :open-on-hover="mdAndUp"
    :open-on-click="mdAndDown"
    @update:modelValue="onToggleToolTip"
    :aria-label="t(copyText)"
  >
    <template #activator="{ props: tooltipProps }">
      <v-btn
        v-bind="tooltipProps"
        class="cursor-pointer rounded-lg"
        variant="text"
        :aria-label="buttonText ? undefined : t(copyText)"
        :title="t(copyText)"
        @click="handleCopy"
        color="on-surface"
        :size="buttonText ? undefined : 48"
        :text="buttonText"
      >
        <template #prepend>
          <FontAwesomeIcon size="lg" :icon="isCopied ? `${iconCopied}` : `${iconCopy}`"></FontAwesomeIcon>
        </template>
      </v-btn>
    </template>
  </v-tooltip>
</template>

<style scoped lang="scss"></style>
